import { useTranslation } from 'react-i18next';

type Band = 'high' | 'good' | 'medium';

/** The match RPC returns a weighted score in [0,1] (distance·availability·rating·
 *  reliability, weights sum to 1). Product rule: never surface the raw number —
 *  map it to a labeled band. */
function bandFor(score: number): Band {
  if (score >= 0.66) return 'high';
  if (score >= 0.4) return 'good';
  return 'medium';
}

const BARS: Record<Band, number> = { high: 3, good: 2, medium: 1 };
const COLOR: Record<Band, string> = {
  high: 'var(--color-success)',
  good: 'var(--color-primary)',
  medium: 'var(--color-accent-text)',
};
const LABEL_KEY: Record<Band, string> = {
  high: 'match.strengthHigh',
  good: 'match.strengthGood',
  medium: 'match.strengthMedium',
};

/**
 * A labeled 3-bar match-strength meter. Meaning is conveyed by bar count **and**
 * text label, never color alone (a11y). The raw score is never shown.
 */
export function MatchStrengthMeter({ score }: { score: number }): JSX.Element {
  const { t } = useTranslation();
  const band = bandFor(score);
  const filled = BARS[band];
  const color = COLOR[band];
  const label = t(LABEL_KEY[band]);

  return (
    <span className="match-meter" aria-label={label}>
      <span className="match-meter__bars" aria-hidden="true">
        {[1, 2, 3].map((n) => (
          <span
            key={n}
            className="match-meter__bar"
            style={{ background: n <= filled ? color : 'var(--color-border)' }}
          />
        ))}
      </span>
      <span className="match-meter__label" style={{ color }}>
        {label}
      </span>
    </span>
  );
}
